import { getAuditRecoNotification } from "./utils"
import { loadSagaTranslation } from "Utils/sagaTranslation"
import { takeEvery, put, call, select } from "redux-saga/effects"
// Constantes
import {
  GET_AUDIT_PLANS,
  GET_AUDIT_PLAN_BY_ID,
  POST_AUDIT_PLAN,
  PATCH_AUDIT_PLAN,
  DUPLICATE_AUDIT_PLAN,
  DELETE_AUDIT_PLAN,
  SUBMIT_AUDIT_PLAN,
} from "./constants"
// Sagas
import { postFileToS3 } from "ContainersForm/ETL/saga"
import {
  manageSuccessGenerator,
  responseTypeIsSuccess,
  manageErrorGenerator,
  responseTypeIsError,
} from "Containers/App/saga"
// Actions
import { postNotificationAction } from "Containers/Notifications/actions.js"
import { setErrorMessage } from "Containers/Messages/actions"
import { getFullAuditRecommendationsAction } from "ContainersForm/NonConformity/actions.js"
import {
  getAuditPlansAction,
  getAuditPlansSuccessAction,
  getAuditPlanByIdSuccessAction,
  getAuditPlanByIdAction,
  setAuditPlanIsLoadingAction,
} from "./actions"
// Selectors
import { selectCurrentToken, selectCurrentUser } from "Containers/Login/selectors"
// Utils
import { getCors, postCors, patchCors, deleteCors } from "Utils/rulesCors"
import { config } from "Utils/config"
import request from "Utils/request"
import { generateTemplateNotification } from "Components/Notifications/utils"

// ------------------------//
// -------- SAGAS ---------//
// ------------------------//

function* getAuditPlansSaga() {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/auditPlans `

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getAuditPlansSuccessAction(response.body))
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* getAuditPlanByIdSaga({ payload }) {
  const { auditPlanId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/auditPlans/${auditPlanId}`

  try {
    yield put(setAuditPlanIsLoadingAction(true))
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getAuditPlanByIdSuccessAction(response.body))
        yield put(setAuditPlanIsLoadingAction(false))
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* postAuditPlanSaga({ payload }) {
  const { auditPlanInWork, auditFilesToPost } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/auditPlans`
  const filesNames = []

  try {
    if (!Array.isArray(auditFilesToPost))
      return console.log("Erreur audit files need to be an array")
    // 1) We loop over the files to add them to the ETL
    for (let file of auditFilesToPost) {
      const responsePostFile = yield call(postFileToS3, file)
      if (responseTypeIsError(responsePostFile)) {
        yield call(manageErrorGenerator, { response: responsePostFile, showMessageToUser: true })
      } else {
        const newFilePosted = {
          name: responsePostFile?.body?.name,
          _id: responsePostFile?.body?._id,
        }
        filesNames.push(newFilePosted)
      }
    }
    const auditPlan = {
      ...auditPlanInWork,
      filesNames: filesNames,
    }
    // 2) We send audit plan to FORM BACK
    const response = yield call(request, requestURL, postCors({ auditPlan }, specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getAuditPlansAction())
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* patchAuditPlanSaga({ payload }) {
  const { auditPlanInWork, auditFilesToPost, auditPlanId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/auditPlans/${auditPlanId}`
  const prevFilesNames = auditPlanInWork?.filesNames
  const filesNames = [...prevFilesNames]

  try {
    if (Array.isArray(auditFilesToPost)) {
      // 1) We loop over the files to add them to the ETL
      for (let file of auditFilesToPost) {
        const responsePostFile = yield call(postFileToS3, file)
        if (responseTypeIsError(responsePostFile)) {
          yield call(manageErrorGenerator, {
            response: responsePostFile,
            showMessageToUser: true,
          })
        } else {
          const newFilePosted = {
            name: responsePostFile?.body?.name,
            _id: responsePostFile?.body?._id,
          }
          filesNames.push(newFilePosted)
        }
      }
    }

    const auditPlan = {
      ...auditPlanInWork,
      filesNames: filesNames,
    }
    const response = yield call(request, requestURL, patchCors({ auditPlan }, specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: true })
        yield put(getAuditPlansAction())
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* duplicateAuditPlanSaga({ payload }) {
  const { auditPlanId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/duplicateAuditPlan/${auditPlanId}`

  try {
    const response = yield call(request, requestURL, postCors({}, specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getAuditPlansAction())
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* deleteAuditPlanSaga({ payload }) {
  const { auditPlanId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/auditPlans/${auditPlanId}`

  try {
    const response = yield call(request, requestURL, deleteCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getAuditPlansAction())
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* submitAuditPlanSaga({ payload }) {
  const { auditPlanId, auditRecommendations } = payload
  const token = yield select(selectCurrentToken)
  const currentUser = yield select(selectCurrentUser)
  const trad = yield call(loadSagaTranslation, currentUser?.LANGUAGE_ID)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/submitAuditPlan/${auditPlanId}`

  try {
    const response = yield call(request, requestURL, postCors({}, specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getAuditPlanByIdAction({ auditPlanId }))
        yield put(getFullAuditRecommendationsAction())
        if (Array.isArray(auditRecommendations)) {
          for (let reco of auditRecommendations) {
            const notif = getAuditRecoNotification(currentUser, trad, reco)
            const infos_notif = generateTemplateNotification("audit_complication_created", {
              notif,
              currentUser,
              payload,
              trad,
            })
            yield put(postNotificationAction({ notif, infos_notif }))
          }
        }
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* auditPlansSaga() {
  yield takeEvery(GET_AUDIT_PLANS, getAuditPlansSaga)
  yield takeEvery(GET_AUDIT_PLAN_BY_ID, getAuditPlanByIdSaga)
  yield takeEvery(POST_AUDIT_PLAN, postAuditPlanSaga)
  yield takeEvery(PATCH_AUDIT_PLAN, patchAuditPlanSaga)
  yield takeEvery(DUPLICATE_AUDIT_PLAN, duplicateAuditPlanSaga)
  yield takeEvery(DELETE_AUDIT_PLAN, deleteAuditPlanSaga)
  yield takeEvery(SUBMIT_AUDIT_PLAN, submitAuditPlanSaga)
}

export default auditPlansSaga
